/**
 * GitHub asset-byte fetcher for the pointer-only install path.
 *
 * Given an upstream repo URL and a pinned commit SHA, walks the recursive
 * git tree at that commit and fetches the blob bytes for each requested path.
 * The Catalog never serves asset bytes — it only hands clients the Pointer
 * Triple (`source.url` + `source.commitSha` + per-file `{path, sha256}`),
 * and `installFromManifest` verifies every byte against those hashes.
 *
 * Authentication: an optional GitHub PAT is read from the `SKAILE_GITHUB_PAT`
 * environment variable when no `octokit` instance is injected. Public repos
 * work unauthenticated (subject to GitHub's anonymous rate limit).
 *
 * @docLink packages/library/concepts#install
 */

import { Octokit } from "@octokit/rest";

/** GitHub PAT environment variable used when no Octokit is injected. */
export const GITHUB_PAT_ENV = "SKAILE_GITHUB_PAT";

/**
 * Options for {@link fetchAssetFilesFromGitHub}.
 */
export interface FetchOpts {
  /** Upstream repo URL (e.g. `https://github.com/skaile-ai/skaile-ai-assets`). */
  url: string;
  /** Immutable 40-char git commit SHA the asset version was pinned at. */
  commitSha: string;
  /** Repo-relative paths to fetch. */
  paths: string[];
  /** Optional Octokit instance — injectable for tests. */
  octokit?: Octokit;
}

/**
 * Parse `{ owner, repo }` out of a GitHub repo URL.
 *
 * Accepts both HTTPS (`https://github.com/owner/repo[.git]`) and SCP-style
 * SSH (`git@github.com:owner/repo[.git]`) forms.
 */
function parseRepo(url: string): { owner: string; repo: string } {
  // Repo names may contain dots (e.g. `skaile-ai/www.skaile.ai`). Capture the
  // full repo segment up to a path separator / end-of-string, and strip an
  // optional trailing `.git` suffix only.
  const m = url.match(/github\.com[/:]([^/]+)\/([^/]+?)(?:\.git)?(?:[/#?].*)?$/i);
  if (!m) throw new Error(`unsupported source URL: ${url}`);
  return { owner: m[1]!, repo: m[2]! };
}

/**
 * Fetch the bytes of each requested path from a GitHub repo at a pinned commit.
 *
 * Reads the recursive git tree at `commitSha`, maps each blob path to its blob
 * SHA, then fetches each requested path's blob and base64-decodes it.
 *
 * @returns A `{ path → Buffer }` map covering every requested path.
 * @throws An error whose message matches `/unreachable/i` if the tree cannot
 *   be read or any requested path is absent from the tree.
 */
export async function fetchAssetFilesFromGitHub(opts: FetchOpts): Promise<Record<string, Buffer>> {
  // The pointer-only integrity model depends on `commitSha` being an immutable
  // git commit SHA. GitHub's tree API also accepts branch/tag names, which
  // would produce a mutable fetch and a non-pinned `Instance.sourceCommitSha` —
  // silently breaking the auditable/re-verifiable guarantee. Reject anything
  // that is not exactly 40 hex characters.
  if (!/^[0-9a-f]{40}$/i.test(opts.commitSha)) {
    throw new Error(
      `pinned commit must be a 40-character hex SHA, got '${opts.commitSha}' — ` +
        "a branch or tag would make the install mutable",
    );
  }
  const octo = opts.octokit ?? new Octokit({ auth: process.env[GITHUB_PAT_ENV] });
  const { owner, repo } = parseRepo(opts.url);

  let tree: Awaited<ReturnType<typeof octo.rest.git.getTree>>;
  try {
    tree = await octo.rest.git.getTree({
      owner,
      repo,
      tree_sha: opts.commitSha,
      recursive: "true",
    });
  } catch (e) {
    throw new Error(`unreachable: cannot read tree at ${opts.commitSha}: ${(e as Error).message}`);
  }

  // path → blob SHA
  const byPath = new Map<string, string>();
  for (const node of tree.data.tree) {
    if (node.type === "blob" && node.path && node.sha) {
      byPath.set(node.path, node.sha);
    }
  }

  const result: Record<string, Buffer> = {};
  for (const path of opts.paths) {
    const blobSha = byPath.get(path);
    if (!blobSha) {
      // [H1] A recursive tree listing is truncated for large repos
      // (`data.truncated: true`). A path missing from a truncated listing is
      // not necessarily absent from the repo — surface a distinct, accurate
      // error rather than the misleading "path not in tree".
      if (tree.data.truncated) {
        throw new Error(
          `tree truncated — repo too large for recursive listing at ${opts.commitSha}`,
        );
      }
      throw new Error(`unreachable: path not in tree: ${path}`);
    }
    const blob = await octo.rest.git.getBlob({ owner, repo, file_sha: blobSha });
    // [L2] GitHub returns `encoding: "none"` (with empty content) for blobs
    // larger than ~1MB. Only base64 is decodable here — fail loudly otherwise.
    if (blob.data.encoding !== "base64") {
      throw new Error(
        `unreachable: blob ${path} has unsupported encoding '${blob.data.encoding}' (likely too large)`,
      );
    }
    result[path] = Buffer.from(blob.data.content, "base64");
  }
  return result;
}
